/**
 * MobileTabBar — phone bottom navigation for the CRM (hidden at lg+, where the
 * sidebar takes over). Recruiters on phones live in four places: the Today
 * worklist, the message queue (Active conversations, badged with the
 * needs-reply backlog), the lead inbox, and Reports. Everything else stays one
 * tap away behind "More", which opens the existing nav drawer.
 *
 * Fixed to the bottom with safe-area padding so it clears the iPhone home
 * indicator when the app is launched from the home screen. z-30: below the
 * full-screen CallWorkspace (z-40) and the softphone/toast overlays, so an
 * active call takes the whole screen. CRMLayout hides it on the lead
 * conversation screen (/crm/contacts/:id) — that page is a focused chat where
 * the thread + keyboard need the room.
 */
import { NavLink } from 'react-router-dom';
import { BarChart3, Inbox, Menu, MessagesSquare, Sun } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type { NavCounts } from '../useNavCounts';
import { CountBadge, Z } from './ui';

interface TabItem {
  to: string;
  end?: boolean;
  icon: LucideIcon;
  label: string;
  badge?: number | null;
  /** Appended inside the badge's aria parens (mirrors Sidebar's NavItem.badgeLabel). */
  badgeLabel?: string;
}

export default function MobileTabBar({ counts, onMore }: { counts: NavCounts; onMore: () => void }) {
  const items: TabItem[] = [
    { to: '/crm', end: true, icon: Sun, label: 'Today', badge: counts.today },
    {
      to: '/crm/active',
      icon: MessagesSquare,
      label: 'Messages',
      badge: counts.conversations?.needs_reply,
      badgeLabel: counts.conversations?.needs_reply
        ? `${counts.conversations.needs_reply} awaiting your reply`
        : undefined,
    },
    { to: '/crm/inbox', icon: Inbox, label: 'Leads' },
    { to: '/crm/reports', icon: BarChart3, label: 'Reports' },
  ];

  const itemCls = (active: boolean) =>
    `flex h-full flex-col items-center justify-center gap-0.5 rounded-lg text-[10px] font-medium transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 ${
      active
        ? 'text-primary-600 dark:text-primary-400'
        : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
    }`;

  return (
    <nav
      aria-label="CRM quick navigation"
      className={`fixed inset-x-0 bottom-0 ${Z.header} border-t border-gray-200 bg-white/95 pb-[env(safe-area-inset-bottom)] backdrop-blur-sm dark:border-gray-700 dark:bg-gray-800/95 lg:hidden`}
    >
      <div className="grid h-14 grid-cols-5">
        {items.map(({ to, end, icon: Icon, label, badge, badgeLabel }) => {
          const showBadge = typeof badge === 'number' && badge > 0;
          return (
            <NavLink
              key={to}
              to={to}
              end={end}
              aria-label={showBadge ? `${label} (${badgeLabel || badge})` : label}
              className={({ isActive }) => itemCls(isActive)}
            >
              <span className="relative">
                <Icon className="h-5 w-5" />
                {showBadge && <CountBadge count={badge} variant="corner" />}
              </span>
              {label}
            </NavLink>
          );
        })}
        <button type="button" onClick={onMore} aria-label="More navigation options" className={itemCls(false)}>
          <Menu className="h-5 w-5" />
          More
        </button>
      </div>
    </nav>
  );
}
